import { useState, useEffect } from 'react';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { Eye, EyeOff, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/core/auth/auth';
import { useToast } from '@/hooks/use-toast';
import { getAuthErrorMessage } from '@/core/auth/passwordValidation';
import { getValidatedNextUrl } from '@/core/auth/redirect-utils';
import { RadiologyLiveIcon } from '@/components/icons/ProductIcons';

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [authTimeout, setAuthTimeout] = useState(false);
  const { user, loading: authLoading, signIn } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const nextUrl = getValidatedNextUrl(searchParams.get('next'));

  // Where to land after auth: a validated cross-product target, or the portal.
  const continueToDestination = () => {
    if (nextUrl && !nextUrl.startsWith('/')) {
      window.location.replace(nextUrl);
    } else {
      navigate(nextUrl ?? '/portal/home', { replace: true });
    }
  };

  // Redirect when auth is fully resolved (user exists and not loading)
  useEffect(() => {
    if (!authLoading && user) {
      continueToDestination();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, authLoading]);

  // Timeout detection for stuck auth (especially on mobile)
  useEffect(() => {
    if (!loading) {
      setAuthTimeout(false);
      return;
    }
    
    const timeoutId = setTimeout(() => {
      if (loading) {
        setAuthTimeout(true);
      }
    }, 10000); // 10 second timeout
    
    return () => clearTimeout(timeoutId);
  }, [loading]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const { error } = await signIn(email, password);

      if (error) {
        toast({
          variant: 'destructive',
          title: 'Sign in failed',
          description: getAuthErrorMessage(error),
        });
        setLoading(false);
        return;
      }

      toast({
        title: 'Welcome back!',
        description: 'You have been signed in successfully.',
      });

      // Deterministic redirect: don't rely only on async auth subscription timing.
      continueToDestination();
      setLoading(false);
    } catch (err) {
      if (import.meta.env.DEV) {
        console.warn('[Login] Unexpected sign-in error:', err);
      }
      toast({
        variant: 'destructive',
        title: 'Sign in failed',
        description: 'Something went wrong while signing in. Please try again.',
      });
      setLoading(false);
    }
  };

  if (!authLoading && user) {
    if (nextUrl && !nextUrl.startsWith('/')) {
      // Cross-product target: full navigation, not a router redirect.
      window.location.replace(nextUrl);
      return null;
    }
    return <Navigate to={nextUrl ?? '/portal/home'} replace />;
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4">

      <div className="relative z-10 w-full max-w-md">
        {/* Back link */}
        <Link 
          to="/" 
          className="inline-flex items-center text-muted-foreground hover:text-foreground mb-8 transition-colors"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to home
        </Link>

        {/* Card */}
        <div className="glass-strong rounded-2xl p-8">
          {/* Logo */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center space-x-2 mb-4">
              <RadiologyLiveIcon size={48} />
            </div>
            <h1 className="text-2xl font-bold">Sign in to RadiologyLive.ai</h1>
            <p className="text-muted-foreground mt-2">Access LiveSpeak™ and other RadiologyLive.ai modules</p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="doctor@hospital.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="bg-muted/50"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="bg-muted/50 pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <div className="flex justify-end">
              <Link to="/auth/forgot-password" className="text-sm text-primary hover:underline">
                Forgot password?
              </Link>
            </div>

            <Button
              type="submit"
              className="w-full bg-primary hover:bg-primary/90"
              disabled={loading}
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </Button>

            {/* Timeout warning for stuck auth (mobile) */}
            {authTimeout && (
              <div className="mt-4 p-3 rounded-lg bg-amber-50 border border-amber-200 dark:bg-amber-950 dark:border-amber-800">
                <p className="text-sm text-amber-700 dark:text-amber-300 text-center">
                  Taking longer than expected.{' '}
                  <button 
                    type="button"
                    onClick={() => window.location.reload()}
                    className="font-medium underline hover:no-underline"
                  >
                    Try again
                  </button>
                </p>
              </div>
            )}
          </form>

          {/* Footer */}
          <p className="text-center text-sm text-muted-foreground mt-6">
            Don't have an account?{' '}
            <Link to="/signup" className="text-primary hover:underline">
              Sign up
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
